/**
 * clubDevState — `?clubState=noClub|leaderWaiting|memberNoSharedSwim|sharedMorning`
 * makes each Club front-door state reachable in a desktop browser without a phone
 * (item 2a visual self-review + VP gate). Honored ONLY in a dev build where there
 * is no native shell: on device the real getClubStatus / listCompanions answers win, always.
 * Same seam pattern as `?firehoseStub=1` (stubFirehose.ts) — a URL param read
 * once, never persisted.
 */
import type { ClubStatusResponse, CompanionSummary } from "@contract/types";
import { hasNativeShell } from "../bridge/client";
import { calendarClubCompanions } from "./club/fixtures/calendarClub";
import { debugSearch } from "./debugSearch";

/** `sharedMorningThree` = sharedMorning plus a third swimmer (roster-tap picker).
 *  `calendarClub` = a six-swimmer club over August 2026 (the Club calendar). */
export const CLUB_DEV_STATES = ["noClub", "leaderWaiting", "memberNoSharedSwim", "sharedMorning", "sharedMorningThree", "calendarClub"] as const;
export type ClubDevState = (typeof CLUB_DEV_STATES)[number];

export function parseClubDevState(search: string, nativeShell: boolean): ClubDevState | null {
  if (nativeShell) return null;
  const raw = new URLSearchParams(search).get("clubState");
  return (CLUB_DEV_STATES as readonly string[]).includes(raw ?? "") ? (raw as ClubDevState) : null;
}

export function devClubState(): ClubDevState | null {
  if (typeof window === "undefined") return null;
  return parseClubDevState(debugSearch(), hasNativeShell());
}

/** The getClubStatus answer each dev state stands in for. */
export function devClubStatus(state: ClubDevState): ClubStatusResponse {
  switch (state) {
    case "noClub":
      return { iCloudOk: true, accountMessage: null, role: "none", optedIn: false, myName: null };
    case "leaderWaiting":
      return { iCloudOk: true, accountMessage: null, role: "leader", optedIn: true, myName: "Scott" };
    case "memberNoSharedSwim":
      return { iCloudOk: true, accountMessage: null, role: "member", optedIn: true, myName: "Ryan" };
    case "sharedMorning":
    case "sharedMorningThree":
    case "calendarClub":
      return { iCloudOk: true, accountMessage: null, role: "leader", optedIn: true, myName: "Scott" };
  }
}

/** The listCompanions answer each dev state stands in for. originIds match the
 *  member ids the fixture club source uses (clubSessionSource.ts) so roster
 *  labels and swim counts line up with the day card. */
export function devCompanions(state: ClubDevState): CompanionSummary[] {
  switch (state) {
    case "noClub":
    case "leaderWaiting":
      return [];
    case "memberNoSharedSwim":
      return [{ originId: "scott", swimmerLabel: "Scott", workoutCount: 3, lastSwimDate: "2026-07-10T06:02:00", schemaVersion: 1 }];
    case "sharedMorning":
      return [{ originId: "ryan", swimmerLabel: "Ryan", workoutCount: 3, lastSwimDate: "2026-07-12T07:02:15", schemaVersion: 1 }];
    case "sharedMorningThree":
      // Three swimmers on one morning: the day card's roster becomes the picker.
      return [
        { originId: "ryan", swimmerLabel: "Ryan", workoutCount: 3, lastSwimDate: "2026-07-12T07:02:15", schemaVersion: 1 },
        { originId: "rachel", swimmerLabel: "Rachel", workoutCount: 1, lastSwimDate: "2026-07-12T07:02:30", schemaVersion: 1 },
      ];
    case "calendarClub":
      return calendarClubCompanions().map((c) => ({
        originId: c.id,
        swimmerLabel: c.name,
        workoutCount: c.count,
        lastSwimDate: c.last,
        schemaVersion: 1,
      }));
  }
}
